import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';
import Select from '../../../components/base/Select';
import { supabase, Shipment, ShipmentUpdate } from '../../../utils/supabase';

export default function ShipmentDetails() {
  const { trackingId } = useParams<{ trackingId: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [updates, setUpdates] = useState<ShipmentUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [showEditShipment, setShowEditShipment] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    location: '',
    status: 'In Transit' as ShipmentUpdate['status'],
    remarks: ''
  });
  const [editForm, setEditForm] = useState({
    sender_name: '',
    receiver_name: '',
    origin: '',
    destination: '',
    weight: 0,
    status: 'Created' as Shipment['status'],
    remarks: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (trackingId) {
      fetchShipmentDetails();
    }
  }, [trackingId]);

  const fetchShipmentDetails = async () => {
    try {
      setLoading(true);
      
      // Fetch shipment details
      const { data: shipmentData, error: shipmentError } = await supabase
        .from('shipments')
        .select('*')
        .eq('tracking_id', trackingId)
        .single();

      if (shipmentError) throw shipmentError;
      setShipment(shipmentData);
      
      // Set edit form with current data
      setEditForm({
        sender_name: shipmentData.sender_name,
        receiver_name: shipmentData.receiver_name,
        origin: shipmentData.origin,
        destination: shipmentData.destination,
        weight: shipmentData.weight,
        status: shipmentData.status,
        remarks: shipmentData.remarks || ''
      });

      // Fetch updates
      const { data: updatesData, error: updatesError } = await supabase
        .from('shipment_updates')
        .select('*')
        .eq('tracking_id', trackingId)
        .order('updated_at', { ascending: false });

      if (updatesError) throw updatesError;
      setUpdates(updatesData || []);
    } catch (error) {
      console.error('Error fetching shipment details:', error);
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!updateForm.location.trim()) {
      alert('Location is required');
      return;
    }

    try {
      setSubmitting(true);
      
      // Add new update
      const { error: updateError } = await supabase
        .from('shipment_updates')
        .insert([{
          tracking_id: trackingId,
          location: updateForm.location.trim(),
          status: updateForm.status,
          remarks: updateForm.remarks.trim() || null
        }]);

      if (updateError) throw updateError;

      // Update shipment status if delivered
      if (updateForm.status === 'Delivered') {
        const { error: shipmentError } = await supabase
          .from('shipments')
          .update({ 
            status: 'Delivered',
            updated_at: new Date().toISOString()
          }, { count: 'exact' })
          .eq('tracking_id', trackingId);

        if (shipmentError) throw shipmentError;
      } else {
        // Update shipment's updated_at timestamp
        const { error: shipmentError } = await supabase
          .from('shipments')
          .update({ updated_at: new Date().toISOString() }, { count: 'exact' })
          .eq('tracking_id', trackingId);

        if (shipmentError) throw shipmentError;
      }

      // Reset form and refresh data
      setUpdateForm({ location: '', status: 'In Transit', remarks: '' });
      setShowAddUpdate(false);
      fetchShipmentDetails();
    } catch (error) {
      console.error('Error adding update:', error);
      alert('Failed to add update. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditShipment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editForm.sender_name.trim() || !editForm.receiver_name.trim() || 
        !editForm.origin.trim() || !editForm.destination.trim() || editForm.weight <= 0) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      const { error } = await supabase
        .from('shipments')
        .update({
          sender_name: editForm.sender_name.trim(),
          receiver_name: editForm.receiver_name.trim(),
          origin: editForm.origin.trim(),
          destination: editForm.destination.trim(),
          weight: editForm.weight,
          status: editForm.status,
          remarks: editForm.remarks.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('tracking_id', trackingId);

      if (error) throw error;

      setShowEditShipment(false);
      fetchShipmentDetails();
    } catch (error) {
      console.error('Error updating shipment:', error);
      alert('Failed to update shipment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShipment = async () => {
    try {
      setDeleting(true);
      
      // Delete shipment updates first
      const { error: updatesError } = await supabase
        .from('shipment_updates')
        .delete()
        .eq('tracking_id', trackingId);

      if (updatesError) throw updatesError;

      // Delete shipment
      const { error: shipmentError } = await supabase
        .from('shipments')
        .delete()
        .eq('tracking_id', trackingId);

      if (shipmentError) throw shipmentError;

      // Navigate back to dashboard
      navigate('/admin');
    } catch (error) {
      console.error('Error deleting shipment:', error);
      alert('Failed to delete shipment. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Created': return 'bg-blue-100 text-blue-800';
      case 'At Port': return 'bg-purple-100 text-purple-800';
      case 'In Transit': return 'bg-yellow-100 text-yellow-800';
      case 'Out for Delivery': return 'bg-orange-100 text-orange-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Created': return 'ri-file-add-line';
      case 'At Port': return 'ri-ship-line';
      case 'In Transit': return 'ri-truck-line';
      case 'Out for Delivery': return 'ri-roadster-line';
      case 'Delivered': return 'ri-checkbox-circle-line';
      default: return 'ri-information-line';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex justify-center items-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-blue-600"></i>
        </div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <div className="text-center py-12">
              <i className="ri-error-warning-line text-4xl text-red-500 mb-4"></i>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Shipment Not Found</h2>
              <p className="text-gray-600 mb-6">The tracking ID you're looking for doesn't exist.</p>
              <Button onClick={() => navigate('/admin')}>
                Back to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-4 mb-4">
                <Button
                  onClick={() => navigate('/admin')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Dashboard
                </Button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Shipment Details
              </h1>
              <p className="text-gray-600">Tracking ID: {shipment.tracking_id}</p>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={() => setShowEditShipment(true)}
                variant="outline"
                className="whitespace-nowrap"
              >
                <i className="ri-edit-line mr-2"></i>
                Edit Shipment
              </Button>
              <Button
                onClick={() => setShowAddUpdate(true)}
                className="whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                Add Update
              </Button>
              <Button
                onClick={() => setShowDeleteModal(true)}
                className="whitespace-nowrap bg-red-600 hover:bg-red-700"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                Delete
              </Button>
            </div>
          </div>
        </div>

        {/* Audit Information Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Audit Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Created By</label>
              <p className="text-sm text-gray-900">{shipment?.created_by || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Creator Email</label>
              <p className="text-sm text-gray-900">{shipment?.created_by_email || 'N/A'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Created At</label>
              <p className="text-sm text-gray-900">
                {shipment?.created_at_timestamp 
                  ? formatDate(shipment.created_at_timestamp)
                  : formatDate(shipment?.created_at || '')
                }
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tracking ID</label>
              <p className="text-sm font-mono text-blue-600">{shipment?.tracking_id}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Shipment Information */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Shipment Information</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Sender</p>
                  <p className="text-gray-900">{shipment.sender_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Receiver</p>
                  <p className="text-gray-900">{shipment.receiver_name}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Origin</p>
                  <p className="text-gray-900">{shipment.origin}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Destination</p>
                  <p className="text-gray-900">{shipment.destination}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-600">Weight</p>
                  <p className="text-gray-900">
                    {shipment.weight} {shipment.mode_of_transportation === 'Air' ? 'kg' : 'cbm'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                    {shipment.status}
                  </span>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium text-gray-600">Created</p>
                <p className="text-gray-900">{new Date(shipment.created_at).toLocaleString()}</p>
              </div>
              
              {shipment.remarks && (
                <div>
                  <p className="text-sm font-medium text-gray-600">Remarks</p>
                  <p className="text-gray-900">{shipment.remarks}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Shipment Timeline */}
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Shipment Timeline</h2>
            
            <div className="relative space-y-6">
              {updates.map((update, index) => (
                <div key={update.id} className="relative flex items-start space-x-3">
                  {/* Timeline line */}
                  {index < updates.length - 1 && (
                    <div className="absolute left-4 top-8 w-0.5 h-full bg-gray-200"></div>
                  )}
                  
                  {/* Status icon */}
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStatusColor(update.status)}`}>
                    <i className={`${getStatusIcon(update.status)} text-sm`}></i>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {update.status}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(update.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600">{update.location}</p>
                    {update.remarks && (
                      <p className="text-sm text-gray-500 mt-1">{update.remarks}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {updates.length === 0 && (
                <div className="text-center py-8">
                  <i className="ri-time-line text-3xl text-gray-400 mb-2"></i>
                  <p className="text-gray-500">No updates yet</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Edit Shipment Modal */}
        {showEditShipment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Shipment</h3>
                
                <form onSubmit={handleEditShipment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Sender Name"
                      value={editForm.sender_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, sender_name: e.target.value }))}
                      required
                    />
                    <Input
                      label="Receiver Name"
                      value={editForm.receiver_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, receiver_name: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Origin"
                      value={editForm.origin}
                      onChange={(e) => setEditForm(prev => ({ ...prev, origin: e.target.value }))}
                      required
                    />
                    <Input
                      label="Destination"
                      value={editForm.destination}
                      onChange={(e) => setEditForm(prev => ({ ...prev, destination: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Weight (kg)"
                      type="number"
                      value={editForm.weight}
                      onChange={(e) => setEditForm(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))}
                      min="0.1"
                      step="0.1"
                      required
                    />
                    <Select
                      label="Status"
                      value={editForm.status}
                      onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value as Shipment['status'] }))}
                      options={[
                        { value: 'Created', label: 'Created' },
                        { value: 'In Transit', label: 'In Transit' },
                        { value: 'Delivered', label: 'Delivered' },
                        { value: 'Cancelled', label: 'Cancelled' }
                      ]}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={editForm.remarks}
                      onChange={(e) => setEditForm(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Additional notes..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowEditShipment(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={submitting}
                    >
                      Update Shipment
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add Update Modal */}
        {showAddUpdate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Shipment Update</h3>
                
                <form onSubmit={handleAddUpdate} className="space-y-4">
                  <Input
                    label="Location"
                    value={updateForm.location}
                    onChange={(e) => setUpdateForm(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Current location"
                    required
                  />
                  
                  <Select
                    label="Status"
                    value={updateForm.status}
                    onChange={(e) => setUpdateForm(prev => ({ ...prev, status: e.target.value as ShipmentUpdate['status'] }))}
                    options={[
                      { value: 'Created', label: 'Created' },
                      { value: 'At Port', label: 'At port' },
                      { value: 'At Airport', label: 'At airport' },
                      { value: 'Waiting for Clearance', label: 'Waiting for clearance' },
                      { value: 'At Pick Up Location', label: 'At pick up location' },
                      { value: 'Ready for Pick Up', label: 'Ready for pick up' },
                      { value: 'Out for Delivery', label: 'Out for delivery' },
                      { value: 'Delivered', label: 'Delivered' }
                    ]}
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={updateForm.remarks}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, remarks: e.target.value }))}
                      placeholder="Additional notes..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                    />
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddUpdate(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={submitting}
                    >
                      Add Update
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-delete-bin-line text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Shipment</h3>
                </div>
                
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete shipment <strong>{shipment.tracking_id}</strong>? 
                  This action cannot be undone and will also delete all tracking updates.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteShipment}
                    loading={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Delete Shipment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
